import { useState } from "react";
import { Box, CircularProgress, TextField } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";

import { PopupActionButton, PopupGhostButton, PopupProcessingButton } from "../PopupButtons";
import { DetailBody, DetailCard, DetailTitle, ErrorNote, figmaButtonSx } from "../Settlement/settlementStyles";
import { reasonFieldSx } from "../Settlement/ChallengeDeedDialog";
import type { usePanelVerdict } from "../../hooks/usePanelVerdict";

// The signature-collection step shared by the three V10.1 judging dialogs. Renders the drawn
// seats, lets the connected wallet sign if it holds one, takes pasted signatures from the other
// seats, and submits once the threshold is met. The state lives in usePanelVerdict; this only
// draws it.
//
// Why paste, and not a second wallet prompt: the seats are three different people with three
// different keys, usually not in the same room. Each signs the same typed data from their own
// wallet (this dialog, on their own machine) and passes the signature to whoever submits. The
// contract does not care who submits — the signatures are the authority — so the last seat to
// sign is the natural submitter and pays the gas.
//
// Since V10.1.2 this is the primary lane only for the pre-mint determination (Governance, which
// the backend does not collect for). For EcoChallenge.recordOutcome the backend collects the
// signatures and submits (VerdictSignatureTally); RecordOutcomeDialog falls back to this only
// when that endpoint cannot be reached.

export type PanelVerdict = ReturnType<typeof usePanelVerdict>;

export interface PanelSignatureCollectorProps {
  verdict: PanelVerdict;
  // Copy on the submit button, e.g. "Record verdict".
  submitLabel: string;
  onCancel: () => void;
  // Optional: text shown under the seats when the connected wallet is not one of them.
  notSeatCopy?: string;
}

const STATUS_COPY: Record<string, string> = {
  loading: "Reading the panel…",
  signing: "Waiting for your wallet signature…",
  submitting: "Submitting the determination — confirm the transaction in your wallet…",
};

const shortAddress = (addr: string) => `${addr.slice(0, 6)}…${addr.slice(-4)}`;

const PanelSignatureCollector = ({ verdict, submitLabel, onCancel, notSeatCopy }: PanelSignatureCollectorProps) => {
  const [pasted, setPasted] = useState("");
  const [copied, setCopied] = useState(false);
  const {
    spec,
    viewer,
    seats,
    threshold,
    signatures,
    signedSeats,
    viewerIsSeat,
    viewerHasSigned,
    canSubmit,
    status,
    error,
    isPending,
    signWithWallet,
    addSignature,
    submit,
  } = verdict;

  const viewerSignature = viewer ? signatures.find((s) => s.signer.toLowerCase() === viewer) : undefined;

  const copyOwnSignature = async () => {
    const own = viewerSignature?.signature;
    if (!own) {
      return;
    }
    try {
      await navigator.clipboard.writeText(own);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked — the signature is still shown in the field below to copy by hand.
    }
  };

  const handlePaste = async () => {
    if (!pasted.trim()) {
      return;
    }
    const ok = await addSignature(pasted);
    if (ok) {
      setPasted("");
    }
  };

  if (status === "loading" || !spec) {
    return (
      <>
        {error && <ErrorNote>{error}</ErrorNote>}
        {status === "loading" ? (
          <PopupProcessingButton disabled variant="contained" fullWidth sx={figmaButtonSx}>
            <CircularProgress size={20} sx={{ color: "white", mr: "10px" }} />
            {STATUS_COPY.loading}
          </PopupProcessingButton>
        ) : null}
        <PopupGhostButton onClick={onCancel} fullWidth>
          Cancel
        </PopupGhostButton>
      </>
    );
  }

  return (
    <>
      <DetailCard>
        <DetailTitle>
          {threshold === 1 ? "Checker" : "Panel"} — {signatures.length} of {threshold} signature
          {threshold === 1 ? "" : "s"}
        </DetailTitle>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", mt: "6px" }}>
          {seats.map((seat) => {
            const signed = signedSeats.has(seat.toLowerCase());
            const isViewer = !!viewer && seat.toLowerCase() === viewer;
            return (
              <Box key={seat} sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {signed ? (
                  <CheckCircleOutlineIcon sx={{ fontSize: 18, color: "#2E7D32" }} />
                ) : (
                  <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: "#999" }} />
                )}
                <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "13px", color: "#000" }}>
                  {shortAddress(seat)}
                </span>
                {isViewer && <span style={{ fontSize: "12px", color: "#888" }}>(you)</span>}
              </Box>
            );
          })}
        </Box>
        {!viewerIsSeat && (
          <DetailBody style={{ marginTop: "8px" }}>
            {notSeatCopy ??
              "Your wallet does not hold a seat. You can still submit once you have pasted enough signatures from the seats above."}
          </DetailBody>
        )}
      </DetailCard>

      {/* The connected wallet's own signature, when it holds a seat. */}
      {viewerIsSeat && !viewerHasSigned && !isPending && (
        <PopupActionButton onClick={signWithWallet} variant="contained" fullWidth sx={figmaButtonSx}>
          Sign this verdict with your wallet
        </PopupActionButton>
      )}
      {viewerHasSigned && viewerSignature && (
        <DetailCard>
          <DetailTitle>Your signature</DetailTitle>
          <DetailBody style={{ wordBreak: "break-all", fontFamily: "'IBM Plex Mono', monospace", fontSize: "12px" }}>
            {viewerSignature.signature}
          </DetailBody>
          <PopupGhostButton onClick={copyOwnSignature} fullWidth sx={{ mt: "6px" }}>
            <ContentCopyOutlinedIcon sx={{ fontSize: 16, mr: "6px" }} />
            {copied ? "Copied" : "Copy signature to send to the submitter"}
          </PopupGhostButton>
        </DetailCard>
      )}

      {/* Signatures from the other seats. */}
      {signatures.length < threshold && !isPending && (
        <>
          <TextField
            fullWidth
            multiline
            minRows={2}
            placeholder={`Paste a signature from another seat (0x…)`}
            value={pasted}
            onChange={(e) => setPasted(e.target.value)}
            sx={reasonFieldSx}
          />
          <PopupGhostButton onClick={handlePaste} disabled={!pasted.trim()} fullWidth>
            Add signature
          </PopupGhostButton>
        </>
      )}

      {error && <ErrorNote>{error}</ErrorNote>}

      {isPending ? (
        <PopupProcessingButton disabled variant="contained" fullWidth sx={figmaButtonSx}>
          <CircularProgress size={20} sx={{ color: "white", mr: "10px" }} />
          {STATUS_COPY[status] ?? "Processing…"}
        </PopupProcessingButton>
      ) : (
        <PopupActionButton onClick={submit} disabled={!canSubmit} variant="contained" fullWidth sx={figmaButtonSx}>
          {canSubmit
            ? submitLabel
            : `${submitLabel} — ${threshold - signatures.length} more signature${
                threshold - signatures.length === 1 ? "" : "s"
              } needed`}
        </PopupActionButton>
      )}
      <PopupGhostButton onClick={onCancel} disabled={isPending} fullWidth>
        Cancel
      </PopupGhostButton>
    </>
  );
};

export default PanelSignatureCollector;
